import 'bootstrap/dist/css/bootstrap.css';
import './styles/_global.scss';

import * as RoutesModule from 'Routes';

import { createContext, useContext } from 'react';

import AccountService from 'services/UserAccount/AccountService';
import AccountStore from 'stores/UserAccount/AccountStore';
import App from './App';
import Area9ProgressService from 'services/CoachSection/progress/Area9ProgressService';
import Area9ProgressStore from 'stores/CoachSection/progress/Area9ProgressStore';
import { AuthenticationService } from 'services/AuthenticationService';
import { AxiosErrorHandler } from 'services/AxiosErrorHandler';
import { AxiosInstance } from 'axios';
import { AxiosServiceConfig } from 'services/AxiosServiceConfig';
import { AxiosServiceFactory } from 'services/AxiosServiceFactory';
import BillingService from 'services/Subscription/BillingService';
import BillingStore from 'stores/Subscription/BillingStore';
import { BrowserRouter } from 'react-router-dom';
import CalculatorService from 'services/Calculator/CalculatorService';
import CalculatorStore from 'stores/Calculator/CalculatorStore';
import CertificateService from 'services/UserAccount/CertificateService';
import CertificateStore from 'stores/UserAccount/CertificateStore';
import ClassesService from 'services/CoachSection/classes/ClassesService';
import ClassesStore from 'stores/CoachSection/classes/ClassesStore';
import { ConfigurationService } from 'services/ConfigurationService';
import InviteStudentsService from 'services/CoachSection/students/InviteStudentsService';
import InviteStudentsStore from 'stores/CoachSection/students/InviteStudentsStore';
import InvoicesService from 'services/Subscription/InvoicesService';
import InvoicesStore from 'stores/Subscription/InvoicesStore';
import LanguagesService from 'services/Languages/LanguagesService';
import ManageUserDataService from 'services/UserAccount/ManageUserDataService';
import ManageUserDataStore from 'stores/UserAccount/ManageUserDataStore';
import MastersEditorService from 'services/Admin/Editors/MastersEditorService';
import MastersService from 'services/Masters/MastersService';
import MastersStore from 'stores/Masters/MastersStore';
import { NotificationsStore } from 'stores/NotificationsStore';
import OrganizationsEditorService from 'services/Admin/Editors/OrganizationsEditorService';
import OrganizationsEditorStore from 'stores/Admin/Editors/OrganizationsEditorStore';
import OrganizationsService from 'services/CoachSection/OrganizationsService';
import OrganizationsStore from 'stores/CoachSection/OrganizationsStore';
import ParametersService from 'services/Admin/Editors/ParametersService';
import ParametersStore from 'stores/Admin/Editors/ParametersStore';
import PartnersEditorService from 'services/Admin/Editors/PartnersEditorService';
import PartnersService from 'services/Partners/PartnersService';
import PartnersStore from 'stores/Partners/PartnersStore';
import ProfileService from 'services/UserAccount/ProfileService';
import SavedCalcTableStore from 'stores/Calculator/SavedCalcTableStore';
import SendInvitationsTestService from 'services/StudentSection/SendInvitationsTestService';
import SendInvitationsTestStore from 'stores/StudentSection/SendInvitationsTestStore';
import StudentsService from 'services/CoachSection/students/StudentsService';
import StudentsStore from 'stores/CoachSection/students/StudentsStore';
import TourAveragesEditorService from 'services/Admin/Editors/TourAveragesEditorService';
import TourAveragesEditorStore from 'stores/Admin/Editors/TourAveragesEditorStore';
import TrackmanEventsEditorService from 'services/Admin/Editors/TrackmanEventsEditorService';
import TrackmanEventsEditorStore from 'stores/Admin/Editors/TrackmanEventsEditorStore';
import TrackmanEventsService from 'services/TrackmanEventsService/TrackmanEventsService';
import TrackmanParametersService from 'services/TrackmanParameters/TrackmanParameters';
import TranslationsService from 'services/Languages/TranslationsService';
import TranslationsStore from 'stores/Languages/TranslationsStore';
import { createRoot } from 'react-dom/client';
import registerServiceWorker from './registerServiceWorker';
import { MediaLibraryStore } from 'stores/Admin/MediaLibraryStore';
import { MediaLibraryService } from 'services/Admin/MediaLibraryService';
import LearningHubService from 'services/LearningHub/LearningHubService';
import { EditCertificationService } from 'services/Admin/Editors/EditCertificationService';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ModuleListService } from 'services/Admin/Editors/ModuleListService';
import { CertificatesService } from 'services/Certificates/CertificatesService';
import { ModuleProgressService } from 'services/CoachSection/progress/ModuleProgressService';
import { WebshopService } from 'services/WebshopService/WebshopService';
import AdminPermissionsService from 'services/Permissions/AdminPermissionsService';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AnalyticsService } from 'services/Admin/AnalyticsService';

const rootElement = document.getElementById('root');
const root = createRoot(rootElement!);

registerServiceWorker();

type Stores = {
  // configuration stores
  errorHandler: AxiosErrorHandler;
  configurationService: ConfigurationService;
  authenticationService: AuthenticationService;
  apiServiceAxiosInstance: AxiosInstance;
  notificationStore: NotificationsStore;
  // project data stores
  certificateStore: CertificateStore;
  organizationsEditorStore: OrganizationsEditorStore;
  classesStore: ClassesStore;
  area9ProgressStore: Area9ProgressStore;
  studentsStore: StudentsStore;
  inviteStudentsStore: InviteStudentsStore;
  invoicesStore: InvoicesStore;
  organizationsStore: OrganizationsStore;
  translationsStore: TranslationsStore;
  mastersStore: MastersStore;
  partnersStore: PartnersStore;
  sendInvitationsTestStore: SendInvitationsTestStore;
  billingStore: BillingStore;
  accountStore: AccountStore;
  savedCalcTableStore: SavedCalcTableStore;
  calculatorStore: CalculatorStore;
  manageUserDataStore: ManageUserDataStore;
  parametersStore: ParametersStore;
  tourAverageEditorStore: TourAveragesEditorStore;
  trackmanEventsEditorStore: TrackmanEventsEditorStore;
  mediaLibraryStore: MediaLibraryStore;
};

type Services = {
  moduleListService: ModuleListService;
  mediaLibraryService: MediaLibraryService;
  editCertificationService: EditCertificationService;
  learningHubService: LearningHubService;
  profileService: ProfileService;
  manageUserDataService: ManageUserDataService;
  adminPermissionsService: AdminPermissionsService;
  trackmanEventsService: TrackmanEventsService;
  trackmanParametersService: TrackmanParametersService;
  webshopService: WebshopService;
  certificatesService: CertificatesService;
  moduleProgressService: ModuleProgressService;
  classesService: ClassesService;
  organizationsService: OrganizationsService;
  area9ProgressService: Area9ProgressService;
  inviteStudentsService: InviteStudentsService;
  studentsService: StudentsService;
  configurationService: ConfigurationService;
  mastersService: MastersService;
  partnersService: PartnersService;
  mastersEditorService: MastersEditorService;
  partnersEditorService: PartnersEditorService;
  languagesService: LanguagesService;
  parametersService: ParametersService;
  accountService: AccountService;
  organizationsEditorService: OrganizationsEditorService;
  analyticsService: AnalyticsService;
};

const StoresContext = createContext<Stores | null>(null);
const ServicesContext = createContext<Services | null>(null);

export const useStores = () => {
  const stores = useContext(StoresContext);
  if (!stores) {
    throw new Error('useStores must be used within a StoreProvider.');
  }
  return stores;
};

export const useServices = () => {
  const services = useContext(ServicesContext);
  if (!services) {
    throw new Error('useServices must be used within a ServiceProvider.');
  }
  return services;
};

async function RenderApp() {
  const errorHandler = new AxiosErrorHandler();
  const configurationService = new ConfigurationService();
  const serviceFactory = new AxiosServiceFactory(configurationService);
  const apiServiceAxiosInstance = await serviceFactory.createInstance();
  const authenticationService = new AuthenticationService();
  const notificationStore = new NotificationsStore();

  // services
  const adminPermissionsService = new AdminPermissionsService(apiServiceAxiosInstance);
  const certificateService = new CertificateService(apiServiceAxiosInstance);
  const organizationsEditorService = new OrganizationsEditorService(apiServiceAxiosInstance);
  const classesService = new ClassesService(apiServiceAxiosInstance);
  const area9ProgressService = new Area9ProgressService(apiServiceAxiosInstance);
  const studentsService = new StudentsService(apiServiceAxiosInstance);
  const inviteStudentsService = new InviteStudentsService(apiServiceAxiosInstance);
  const invoicesService = new InvoicesService(apiServiceAxiosInstance);
  const organizationsService = new OrganizationsService(apiServiceAxiosInstance);
  const languagesService = new LanguagesService(apiServiceAxiosInstance);
  const mastersService = new MastersService(apiServiceAxiosInstance);
  const partnersService = new PartnersService(apiServiceAxiosInstance);
  const sendInvitationsTestService = new SendInvitationsTestService(apiServiceAxiosInstance);
  const billingService = new BillingService(apiServiceAxiosInstance);
  const profileService = new ProfileService(apiServiceAxiosInstance);
  const accountService = new AccountService(apiServiceAxiosInstance);
  const calculatorService = new CalculatorService(apiServiceAxiosInstance);
  const manageUserDataService = new ManageUserDataService(apiServiceAxiosInstance);
  const translationsService = new TranslationsService(apiServiceAxiosInstance);
  const parametersService = new ParametersService(apiServiceAxiosInstance);
  const trackmanParametersService = new TrackmanParametersService(apiServiceAxiosInstance);
  const partnersEditorService = new PartnersEditorService(apiServiceAxiosInstance);
  const mastersEditorService = new MastersEditorService(apiServiceAxiosInstance);
  const tourAveragesEditorService = new TourAveragesEditorService(apiServiceAxiosInstance);
  const trackmanEventsEditorService = new TrackmanEventsEditorService(apiServiceAxiosInstance);
  const trackmanEventsService = new TrackmanEventsService(apiServiceAxiosInstance);
  const learningHubService = new LearningHubService(apiServiceAxiosInstance);
  const mediaLibraryService = new MediaLibraryService(apiServiceAxiosInstance);
  const editCertificationService = new EditCertificationService(apiServiceAxiosInstance);
  const moduleListService = new ModuleListService(apiServiceAxiosInstance);
  const webshopService = new WebshopService(apiServiceAxiosInstance);
  const certificatesService = new CertificatesService(apiServiceAxiosInstance);
  const moduleProgressService = new ModuleProgressService(apiServiceAxiosInstance);
  const analyticsService = new AnalyticsService(apiServiceAxiosInstance);

  // stores
  const certificateStore = new CertificateStore(certificateService, notificationStore);
  const organizationsEditorStore = new OrganizationsEditorStore(organizationsEditorService, notificationStore);
  const area9ProgressStore = new Area9ProgressStore(area9ProgressService, notificationStore);
  const organizationsStore = new OrganizationsStore(organizationsService, notificationStore);
  const invoicesStore = new InvoicesStore(invoicesService, notificationStore);
  const classesStore = new ClassesStore(classesService, organizationsStore, notificationStore);
  const inviteStudentsStore = new InviteStudentsStore(inviteStudentsService, organizationsStore, classesStore, notificationStore);
  const studentsStore = new StudentsStore(studentsService, organizationsStore, classesStore, notificationStore);
  const translationsStore = new TranslationsStore(translationsService, notificationStore);
  const mastersStore = new MastersStore(mastersService, notificationStore);
  const partnersStore = new PartnersStore(partnersService, notificationStore);
  const sendInvitationsTestStore = new SendInvitationsTestStore(sendInvitationsTestService, notificationStore);
  const billingStore = new BillingStore(billingService, notificationStore);
  const accountStore = new AccountStore(accountService, notificationStore);
  const savedCalcTableStore = new SavedCalcTableStore(notificationStore);
  const calculatorStore = new CalculatorStore(calculatorService, notificationStore);
  const manageUserDataStore = new ManageUserDataStore(manageUserDataService, notificationStore);
  const parametersStore = new ParametersStore(parametersService, notificationStore);
  const tourAverageEditorStore = new TourAveragesEditorStore(tourAveragesEditorService, notificationStore);
  const trackmanEventsEditorStore = new TrackmanEventsEditorStore(trackmanEventsEditorService, notificationStore);
  const mediaLibraryStore = new MediaLibraryStore(mediaLibraryService);

  const stores: Stores = {
    // configuration stores
    errorHandler,
    configurationService,
    authenticationService,
    apiServiceAxiosInstance,
    notificationStore,
    // project data stores
    certificateStore,
    organizationsEditorStore,
    classesStore,
    area9ProgressStore,
    studentsStore,
    invoicesStore,
    inviteStudentsStore,
    organizationsStore,
    translationsStore,
    mastersStore,
    partnersStore,
    sendInvitationsTestStore,
    billingStore,
    accountStore,
    savedCalcTableStore,
    calculatorStore,
    manageUserDataStore,
    parametersStore,
    tourAverageEditorStore,
    trackmanEventsEditorStore,
    mediaLibraryStore,
  };

  const services: Services = {
    moduleListService: moduleListService,
    mediaLibraryService: mediaLibraryService,
    editCertificationService: editCertificationService,
    learningHubService: learningHubService,
    profileService: profileService,
    adminPermissionsService: adminPermissionsService,
    trackmanEventsService: trackmanEventsService,
    trackmanParametersService: trackmanParametersService,
    webshopService: webshopService,
    certificatesService: certificatesService,
    moduleProgressService: moduleProgressService,
    classesService: classesService,
    organizationsService: organizationsService,
    area9ProgressService: area9ProgressService,
    inviteStudentsService: inviteStudentsService,
    studentsService: studentsService,
    manageUserDataService: manageUserDataService,
    configurationService: configurationService,
    mastersService: mastersService,
    partnersService: partnersService,
    mastersEditorService: mastersEditorService,
    partnersEditorService: partnersEditorService,
    languagesService: languagesService,
    parametersService: parametersService,
    accountService: accountService,
    organizationsEditorService: organizationsEditorService,
    analyticsService: analyticsService,
  };

  translationsStore.initialize();

  // Add a request and response interceptor
  AxiosServiceConfig.setDefaultDecorators(apiServiceAxiosInstance);
  const queryClient = new QueryClient();

  root.render(
    <BrowserRouter>
      <StoresContext.Provider value={stores}>
        <ServicesContext.Provider value={services}>
          <QueryClientProvider client={queryClient}>
            <App>{RoutesModule.routes}</App>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </ServicesContext.Provider>
      </StoresContext.Provider>
    </BrowserRouter>
  );
}

RenderApp();
